import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  House, Users, ListBullets, Funnel, FileText, PaperPlaneTilt,
  ChartBar, ShieldSlash, Gear, MagnifyingGlass, Buildings, BookOpen, Question, LockKey, SignOut, Heartbeat, FlowArrow, SidebarSimple, Binoculars, Broom,
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { CommandPalette } from "./CommandPalette";

const NAV = [
  { to: "/", label: "Dashboard", icon: House, end: true },
  { to: "/contacts", label: "Contacts", icon: Users },
  { to: "/companies", label: "Companies", icon: Buildings },
  { to: "/prospect", label: "Prospect", icon: Binoculars },
  { to: "/cleanup", label: "Cleanup", icon: Broom },
  { to: "/lists", label: "Lists", icon: ListBullets },
  { to: "/segments", label: "Segments", icon: Funnel },
  { to: "/templates", label: "Templates", icon: FileText },
  { to: "/campaigns", label: "Campaigns", icon: PaperPlaneTilt },
  { to: "/sequences", label: "Sequences", icon: FlowArrow },
  { to: "/publications", label: "Publications", icon: LockKey },
  { to: "/analytics", label: "Analytics", icon: ChartBar },
  { to: "/health", label: "Sender Health", icon: Heartbeat },
  { to: "/suppressions", label: "Suppressions", icon: ShieldSlash },
  { to: "/settings", label: "Settings", icon: Gear },
  { to: "/guide", label: "Guide", icon: BookOpen },
];

export const AppShell = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("medihub_sidebar_collapsed") === "1");
  const current = NAV.find(n => n.end ? location.pathname === "/" : location.pathname.startsWith(n.to));

  const toggleSidebar = () => {
    setCollapsed(c => {
      localStorage.setItem("medihub_sidebar_collapsed", c ? "0" : "1");
      return !c;
    });
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(o => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className={`app-shell ${collapsed ? "sidebar-collapsed" : ""}`}>
      <aside className={`border-r border-zinc-200 bg-white py-4 flex flex-col gap-1 sticky top-0 h-screen overflow-hidden ${collapsed ? "px-2" : "px-3"}`} data-testid="sidebar">
        <Link to="/" className={`flex items-center gap-2 py-3 mb-2 ${collapsed ? "justify-center px-0" : "px-2"}`} data-testid="brand-link" title="MediHub">
          <div className="w-7 h-7 rounded-md bg-zinc-950 flex items-center justify-center shrink-0">
            <PaperPlaneTilt size={16} weight="fill" className="text-white" />
          </div>
          {!collapsed && <span className="font-heading text-[15px] font-semibold tracking-tight whitespace-nowrap">MediHub</span>}
        </Link>
        <nav className="flex-1 flex flex-col gap-0.5 overflow-y-auto">
          {NAV.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase()}`}
              title={collapsed ? item.label : undefined}
              className={({isActive}) => `flex items-center gap-2.5 py-2 rounded-md text-sm font-medium transition-colors ${collapsed ? "justify-center px-0" : "px-2.5"} ${isActive ? "bg-zinc-100 text-zinc-950" : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"}`}
            >
              <item.icon size={collapsed ? 18 : 16} weight={location.pathname === item.to ? "fill" : "regular"} className="shrink-0" />
              {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className={`border-t border-zinc-200 pt-3 ${collapsed ? "px-0" : "px-1"}`} data-testid="user-chip">
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center text-[11px] font-semibold text-zinc-700" title={user?.name || user?.email}>
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <button data-testid="logout-btn" onClick={logout} title="Sign out"
                className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors">
                <SignOut size={15} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center text-[11px] font-semibold text-zinc-700 shrink-0">
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-medium text-zinc-900 truncate">{user?.name || user?.email}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{user?.role}</div>
              </div>
              <button
                data-testid="logout-btn"
                onClick={logout}
                title="Sign out"
                className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors shrink-0"
              >
                <SignOut size={15} />
              </button>
            </div>
          )}
        </div>
      </aside>

      <main className="app-main">
        <header className="sticky top-0 z-10 border-b border-zinc-200 bg-white/80 backdrop-blur-xl px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="sidebar-toggle"
              onClick={toggleSidebar}
              title={collapsed ? "Expand menu" : "Collapse menu"}
              className="p-1.5 -ml-1.5 rounded-md text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
            >
              <SidebarSimple size={17} />
            </button>
            <h1 className="font-heading text-[15px] font-medium text-zinc-900" data-testid="page-title">
              {current?.label || ""}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="global-search"
              onClick={() => setPaletteOpen(true)}
              className="h-8 w-72 rounded-md border border-zinc-200 bg-white pl-2.5 pr-2 text-sm text-zinc-400 flex items-center gap-2 hover:border-zinc-300 transition-colors"
            >
              <MagnifyingGlass size={14} className="shrink-0" />
              <span className="flex-1 text-left">Search anything…</span>
              <kbd className="text-[10px] font-semibold text-zinc-400 border border-zinc-200 rounded px-1.5 py-0.5 bg-zinc-50">⌘K</kbd>
            </button>
          </div>
        </header>
        <div className="flex-1 px-6 py-6 anim-in">{children}</div>
      </main>
      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />
    </div>
  );
};
